import axios from "axios";

const API_BASE_URL = "https://qa.stemx365.org";

// ============================================================
// AXIOS INSTANCE
// ============================================================

const apiClient = axios.create({
  baseURL: API_BASE_URL,

  headers: {
    "Content-Type": "application/json",
  },

  // Send cookies/session credentials
  withCredentials: true,
});


// ============================================================
// AUTH HEADERS
// ============================================================

const getAuthHeaders = () => {

  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("apiKey");

  return token
    ? {
        Authorization: `Bearer ${token}`,
      }
    : {};
};


// ============================================================
// GENERIC API SERVICE
// ============================================================

const ApiService = {

  // ==========================================================
  // GET ALL
  // ==========================================================

  getAll: async (
    resource,
    needsAuth = true,
    parent = null
  ) => {

    try {

      let url =
        `/api/v1/${resource}/`;

      if (parent) {

        url =
          `/api/v1/${parent.type}/${parent.id}/${resource}/`;

      }

      const response =
        await apiClient.get(url, {

          headers:
            needsAuth
              ? getAuthHeaders()
              : {},

          // Explicitly send cookies
          withCredentials: true,

        });

      return response.data || [];

    } catch (err) {

      console.error(
        `Error fetching ${resource}:`,
        err
      );

      throw err;
    }
  },


  // ==========================================================
  // GET ONE
  // ==========================================================

  getOne: async (
    resource,
    id,
    parent = null,
    needsAuth = true
  ) => {

    if (!id) {
      throw new Error(
        "ID is required"
      );
    }

    try {

      let url =
        `/api/v1/${resource}/${id}/`;

      if (parent) {

        url =
          `/api/v1/${parent.type}/${parent.id}/${resource}/${id}/`;

      }

      console.log(
        "GET URL:",
        url
      );

      const response =
        await apiClient.get(url, {

          headers:
            needsAuth
              ? getAuthHeaders()
              : {},

          withCredentials: true,

        });

      return response.data || {};

    } catch (err) {

      console.error(
        `Error fetching ${resource} with ID ${id}:`,
        err
      );

      throw err;
    }
  },


  // ==========================================================
  // CREATE
  // ==========================================================

  createItem: async (
    resource,
    data,
    needsAuth = true,
    parent = null
  ) => {

    if (!data) {

      throw new Error(
        "Data is required for creation"
      );

    }

    try {

      let url =
        `/api/v1/${resource}/`;

      if (parent) {

        url =
          `/api/v1/${parent.type}/${parent.id}/${resource}/`;

      }

      const response =
        await apiClient.post(
          url,
          data,
          {

            headers:
              needsAuth
                ? getAuthHeaders()
                : {},

            withCredentials: true,

          }
        );

      return response.data;

    } catch (err) {

      console.error(
        `Error creating ${resource}:`,
        err
      );

      throw err;
    }
  },


  // ==========================================================
  // UPDATE
  // ==========================================================

  updateItem: async (
    resource,
    id,
    data,
    parent = null,
    needsAuth = true
  ) => {

    if (!id || !data) {

      throw new Error(
        "ID and data are required for update"
      );

    }

    try {

      let url =
        `/api/v1/${resource}/${id}/`;

      if (parent) {

        url =
          `/api/v1/${parent.type}/${parent.id}/${resource}/${id}/`;

      }

      const response =
        await apiClient.put(
          url,
          data,
          {

            headers:
              needsAuth
                ? getAuthHeaders()
                : {},

            withCredentials: true,

          }
        );

      return response.data;

    } catch (err) {

      console.error(
        `Error updating ${resource} with ID ${id}:`,
        err
      );

      throw err;
    }
  },


  // ==========================================================
  // DELETE
  // ==========================================================

  deleteItem: async (
    resource,
    id,
    needsAuth = true,
    parent = null
  ) => {

    if (!id) {

      throw new Error(
        "ID is required for deletion"
      );

    }

    try {

      let url =
        `/api/v1/${resource}/${id}/`;

      if (parent) {

        url =
          `/api/v1/${parent.type}/${parent.id}/${resource}/${id}/`;

      }

      const response =
        await apiClient.delete(
          url,
          {

            headers:
              needsAuth
                ? getAuthHeaders()
                : {},

            withCredentials: true,

          }
        );

      return (
        response.data || {
          message:
            "Deleted successfully",
        }
      );

    } catch (err) {

      console.error(
        `Error deleting ${resource} with ID ${id}:`,
        err
      );

      throw err;
    }
  },


  // ==========================================================
  // LOGOUT
  // ==========================================================

  logoutUser: async () => {

    try {

      const user =
        JSON.parse(
          localStorage.getItem(
            "user"
          ) || "null"
        );

      const response =
        await apiClient.post(
          "/logout",
          user,
          {
            headers:
              getAuthHeaders(),

            withCredentials: true,
          }
        );

      localStorage.clear();

      return (
        response.data || []
      );

    } catch (err) {

      console.error(
        "Error logging out:",
        err
      );

      throw err;
    }
  },


  // ==========================================================
  // CHECK SESSION
  // ==========================================================

  checkSession: async () => {

    try {

      const response =
        await apiClient.get(
          "/check-session",
          {

            headers:
              getAuthHeaders(),

            withCredentials: true,

          }
        );

      return (
        response.data
          ?.session_active ||
        false
      );

    } catch (err) {

      console.error(
        "Error checking session:",
        err
      );

      throw err;
    }
  },


  // ==========================================================
  // REGISTER INDIVIDUAL
  // ==========================================================

  registerIndividual: async (
    data
  ) => {

    try {

      const response =
        await apiClient.post(
          "/register/individual",
          data,
          {

            headers:
              getAuthHeaders(),

            withCredentials: true,

          }
        );

      return (
        response.data || []
      );

    } catch (error) {

      console.error(
        "Error creating new project:",
        error
      );

      throw error;
    }
  },

};


export default ApiService;